/**
 * Admin API service for admin-only endpoints.
 * All endpoints require admin role authentication.
 */
import api from './api';

export const adminAPI = {
  /**
   * Get dashboard statistics.
   * @returns {Promise} Dashboard stats including shipments, users, sessions
   */
  getStats: () => api.get('/admin/stats'),

  /**
   * Get all users with pagination and filtering.
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.per_page - Items per page
   * @param {string} params.search - Search term
   * @param {string} params.role - Filter by role
   * @returns {Promise} Users list with pagination
   */
  getUsers: (params) => api.get('/admin/users', { params }),

  /**
   * Create new user.
   * @param {Object} data - User data
   * @param {string} data.user_id - User ID
   * @param {string} data.password - Password
   * @param {string} data.name - Full name
   * @param {string} data.email - Email address
   * @param {string} data.role - Role (user/admin)
   * @returns {Promise} Created user info
   */
  createUser: (data) => api.post('/admin/users', data),

  /**
   * Update user details.
   * @param {number} id - User ID
   * @param {Object} data - Updated user data
   * @returns {Promise} Update confirmation
   */
  updateUser: (id, data) => api.post(`/admin/users/${id}`, data),

  /**
   * Get active user sessions.
   * @returns {Promise} List of active sessions
   */
  getSessions: () => api.get('/admin/sessions'),

  /**
   * Get system health metrics.
   * @returns {Promise} System health data
   */
  getSystemHealth: () => api.get('/admin/system-health'),

  /**
   * Get audit log entries.
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.per_page - Items per page
   * @returns {Promise} Audit log entries
   */
  getAuditLog: (params) => api.get('/admin/audit-log', { params }),

  /**
   * Get all shipments from all users.
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.per_page - Items per page
   * @param {string} params.search - Search term
   * @param {string} params.user_id - Filter by user
   * @param {string} params.status - Filter by status
   * @param {string} params.type - Filter by type
   * @param {string} params.date_from - Start date
   * @param {string} params.date_to - End date
   * @returns {Promise} All shipments with pagination
   */
  getAllShipments: (params) => api.get('/admin/shipments', { params }),

  /**
   * Reset user password (auto-generates and emails password).
   * @param {number} userId - User ID to reset password for
   * @returns {Promise} Reset confirmation
   */
  resetUserPassword: (userId) =>
    api.post(`/admin/users/${userId}/reset-password`, {}),

  /**
   * Delete user (super_admin only).
   * @param {number} userId - User ID to delete
   * @returns {Promise} Delete confirmation
   */
  deleteUser: (userId) => api.post(`/admin/users/${userId}/delete`),
};
